import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { HttpService, ApiModels } from '@mona/api';
import { Encounter, MedicationAdministration, PageableModel } from '@mona/models';
import { transformApiMedicationAdministrations } from './transforms';

/**
 * API abstraction layer for the medication administrations API
 */
@Injectable({
    providedIn: 'root',
})
export class MedicationAdministrationsApi {
    apiBase = '/pdms/v2/medication-administrations/';

    /**
     * Constructor
     * @param http HttpClient
     */
    constructor(private http: HttpService) {}

    /**
     * Get all medication administrations for an encounter.
     * @param encounterId EntityId<Encounter>
     */
    getMedicationAdministrations(encounterId: EntityId<Encounter>): Observable<MedicationAdministration[]> {
        return this.fetchAllPages({ encounter_id: encounterId as string }).pipe(map(page => page.results ?? []));
    }

    /**
     * Follow DRF cursor `next` until exhausted, accumulating transformed results.
     */
    private fetchAllPages(
        params: { encounter_id: string } | null,
        url: string = this.apiBase,
    ): Observable<PageableModel<MedicationAdministration>> {
        return this.http
            .get<PageableModel<ApiModels.MedicationAdministration>>(url, {
                params: { ...params },
            })
            .pipe(
                map(data => ({
                    ...data,
                    results: transformApiMedicationAdministrations(data.results ?? []),
                })),
                concatMap(res => {
                    if (res.next) {
                        return this.fetchAllPages(null, res.next).pipe(
                            map(nextData => ({
                                ...nextData,
                                results: [...(res.results ?? []), ...(nextData.results ?? [])],
                            })),
                        );
                    }
                    return of(res);
                }),
            );
    }
}
